export const getAppPageTitle = (name?: string): string => {
  name = name ?? 'OuraHub Portal'

  const env = import.meta.env.VITE_APP_ENV

  if (env === 'release') return name

  return `${env === 'staging' ? 'Staging' : 'Sandbox'} ${name}`
}

// Active preset id by precedence: session override (URL bootstrap or manual pick) →
// saved pref → fallback. Pure (caller passes the sources) so the precedence is unit-testable.
export const resolvePresetId = (sources: { override?: string; savedPreset?: string; fallback: string }): string =>
  sources.override ?? sources.savedPreset ?? sources.fallback

const GROUP_PRESETS: Record<string, string> = {
  groupDarwinUsers: 'darwin',
  groupBpoDarwinUsers: 'darwin',
  groupPoirotUsers: 'poirot',
  groupWaltariUsers: 'waltari',
}

// Returns a preset only when the user's groups map to exactly one of the group types (unrelated groups are ignored). Otherwise undefined.
export const getGroupPresetId = (activeGroups: readonly string[]): string | undefined => {
  const presets = new Set(activeGroups.map((g) => GROUP_PRESETS[g]).filter((p) => p !== undefined))

  return presets.size === 1 ? [...presets][0] : undefined
}

// Splits a hash into its `env/service` path and query string (without `#`/`?`).
// Single source of truth so callers don't hand-roll `slice`/`split` on the hash.
const splitHash = (hash: string): { path: string; query: string } => {
  const body = hash.startsWith('#') ? hash.slice(1) : hash
  const queryIndex = body.indexOf('?')

  if (queryIndex === -1) return { path: body, query: '' }

  return { path: body.slice(0, queryIndex), query: body.slice(queryIndex + 1) }
}

// Returns the `env/service` path portion of a hash, stripping any query suffix.
export const getPathFromHash = (hash: string): string => splitHash(hash).path

// Returns the query string portion of a hash (without the leading `?`), or ''.
export const getQueryFromHash = (hash: string): string => splitHash(hash).query

// Reads the `preset` query param from a hash; undefined when absent or empty.
export const getPresetFromHash = (hash: string): string | undefined =>
  new URLSearchParams(getQueryFromHash(hash)).get('preset') || undefined

const setHashParam = (hash: string, key: string, value?: string): string => {
  const { path, query } = splitHash(hash)
  const params = new URLSearchParams(query)

  if (value) {
    params.set(key, value)
  } else {
    params.delete(key)
  }

  const queryString = params.toString()

  if (!path && !queryString) return ''

  return `#${path}${queryString ? `?${queryString}` : ''}`
}

// Sets the `preset` query param (or removes it when presetId is falsy), keeping the
// path and any other query params.
export const setPresetInHash = (hash: string, presetId?: string): string => setHashParam(hash, 'preset', presetId)

export const getSearchFromHash = (hash: string): string | undefined =>
  new URLSearchParams(getQueryFromHash(hash)).get('q') || undefined

export const setSearchInHash = (hash: string, query?: string): string => setHashParam(hash, 'q', query)

const PENDING_PRESET_KEY = 'ouraHubPendingPreset'

// First-load read: takes `preset` from the hash query (e.g. #sandbox?preset=poirot), stashes it,
// and strips it from the URL so the preset is never shown. Idempotent and safe to call before
// auth — the stash survives the auth redirect (signInWithRedirect) so the value can be persisted
// once the user is known.
export const captureInitialPreset = (): void => {
  const preset = getPresetFromHash(window.location.hash)

  if (!preset) return

  sessionStorage.setItem(PENDING_PRESET_KEY, preset)

  window.location.hash = setPresetInHash(window.location.hash, undefined)
}

// Returns and clears the preset stashed by captureInitialPreset.
export const consumePendingPreset = (): string | undefined => {
  const preset = sessionStorage.getItem(PENDING_PRESET_KEY) || undefined

  sessionStorage.removeItem(PENDING_PRESET_KEY)

  return preset
}

const PENDING_SEARCH_KEY = 'ouraHubPendingSearch'

// Stash `q` from the hash and strip it so it never persists in the URL.
export const captureInitialSearch = (): void => {
  const query = getSearchFromHash(window.location.hash)

  if (!query) return

  sessionStorage.setItem(PENDING_SEARCH_KEY, query)

  window.location.hash = setSearchInHash(window.location.hash, undefined)
}

export const consumePendingSearch = (): string | undefined => {
  const query = sessionStorage.getItem(PENDING_SEARCH_KEY) || undefined

  sessionStorage.removeItem(PENDING_SEARCH_KEY)

  return query
}
